import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Container, Grid, TextField, MenuItem, TableRow, TableCell, IconButton, Tooltip, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/EditOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { toast } from 'react-toastify';
import moment from "moment";
import api from '../../../services/api.js';
import { pode, mensagemErro } from '../../../services/acesso';
import PageHeader from '../../../components/PageHeader';
import FilterBar from '../../../components/FilterBar';
import ConfirmDialog from '../../../components/ConfirmDialog';
import TabelaPaginada, { lerPaginacao } from '../../../components/TabelaPaginada';

const POR_PAGINA = 25;
const STATUS = [
  { valor: "", nome: "Todos" },
  { valor: "true", nome: "Ativo" },
  { valor: "false", nome: "Inativo" },
];
const FILTROS_INICIAIS = { name: "", segmento: "", status: "true" };

const formatarCnpj = (valor) => {
  const d = String(valor || "").replace(/\D/g, "");
  if (d.length !== 14) return valor || "";
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

/**
 * Clientes/projetos.
 * Correções: a paginação começava na página 0 (nenhum número marcado) e Pesquisar não
 * atualizava o total de páginas nem voltava para a página 1.
 */
export default function ListingCustomer() {
  const history = useHistory();
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [aplicados, setAplicados] = useState(FILTROS_INICIAIS);
  const [page, setPage] = useState(1);
  const [dados, setDados] = useState({ linhas: [], total: 0, totalPaginas: 0, porPagina: POR_PAGINA });
  const [loading, setLoading] = useState(true);
  const [excluir, setExcluir] = useState(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let ativo = true;
    setLoading(true);
    api.get("customers", {
      params: {
        page,
        pageSize: POR_PAGINA,
        name: aplicados.name || undefined,
        segmento: aplicados.segmento || undefined,
        status: aplicados.status === "" ? undefined : aplicados.status === "true",
      }
    })
      .then((result) => { if (ativo) setDados(lerPaginacao(result, POR_PAGINA)); })
      .catch((e) => { if (ativo) toast.error(mensagemErro(e, "Não foi possível carregar os clientes")); })
      .finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, [page, aplicados, versao]);

  const aplicar = (novos) => {
    setAplicados(novos);
    setPage(1);
    setVersao((v) => v + 1);
  };

  const limpar = () => {
    const vazios = { name: "", segmento: "", status: "" };
    setFiltros(vazios);
    aplicar(vazios);
  };

  const remover = (campo) => () => {
    const novos = { ...aplicados, [campo]: "" };
    setFiltros(novos);
    aplicar(novos);
  };

  const ativos = [
    aplicados.name && { rotulo: `Nome: ${aplicados.name}`, remover: remover("name") },
    aplicados.segmento && { rotulo: `Segmento: ${aplicados.segmento}`, remover: remover("segmento") },
    aplicados.status !== "" && { rotulo: `Status: ${aplicados.status === "true" ? "Ativo" : "Inativo"}`, remover: remover("status") },
  ].filter(Boolean);

  const handleDeleteClick = () => {
    api.delete(`customers/${excluir.id}`).then(() => {
      toast.success("Cliente excluído");
      setVersao((v) => v + 1);
    }).catch((e) => toast.error(mensagemErro(e, "Não foi possível excluir o cliente")));
  };

  return (
    <Container maxWidth="lg">
      <PageHeader
        titulo="Clientes"
        acoes={pode("editar_clientes") && (
          <Button variant="contained" color="primary" startIcon={<AddIcon />} component={Link} to="/customer/new">
            Novo cliente
          </Button>
        )}
      />

      <FilterBar onPesquisar={() => aplicar(filtros)} onLimpar={limpar} ativos={ativos}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={5}>
            <TextField id="filtro_nome" label="Nome fantasia" value={filtros.name} onChange={(e) => setFiltros({ ...filtros, name: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField id="filtro_segmento" label="Segmento" value={filtros.segmento} onChange={(e) => setFiltros({ ...filtros, segmento: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField id="filtro_status" select label="Status" value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })} fullWidth>
              {STATUS.map((s) => <MenuItem key={s.nome} value={s.valor}>{s.nome}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </FilterBar>

      <TabelaPaginada
        rotulo="Lista de clientes"
        colunas={[
          { titulo: "Nome" }, { titulo: "Segmento" }, { titulo: "Razão social" }, { titulo: "CNPJ" },
          { titulo: "Status" }, { titulo: "Criado em" }, { titulo: "Ações", align: "right" },
        ]}
        carregando={loading}
        vazio={dados.linhas.length === 0}
        textoVazio="Nenhum cliente encontrado"
        temFiltro={ativos.length > 0}
        onLimpar={limpar}
        pagina={page}
        totalPaginas={dados.totalPaginas}
        totalRegistros={dados.total}
        porPagina={dados.porPagina}
        onPagina={setPage}
      >
        {dados.linhas.map((row) => (
          <TableRow key={row.id} hover style={{ cursor: "pointer" }} onClick={() => history.push("/customer/" + row.id + "/edit")}>
            <TableCell style={{ fontWeight: 600 }}>{row.name}</TableCell>
            <TableCell>{row.segmento}</TableCell>
            <TableCell>{row.corporate_name}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{formatarCnpj(row.cnpj)}</TableCell>
            <TableCell>
              <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: row.active ? "#E4EFDF" : "#EEF1EC", color: row.active ? "#1F4615" : "#3D4A38" }}>
                {row.active ? "Ativo" : "Inativo"}
              </span>
            </TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{row.created_at ? moment(row.created_at, "YYYY-MM-DD HH:mm").format("DD/MM/YYYY") : ""}</TableCell>
            <TableCell align="right" style={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Abrir cliente">
                <IconButton size="small" component={Link} to={"/customer/" + row.id + "/edit"} aria-label={`Abrir ${row.name}`}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {pode("excluir") && (
                <Tooltip title="Excluir cliente">
                  <IconButton size="small" aria-label={`Excluir ${row.name}`} onClick={() => setExcluir(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TabelaPaginada>

      <ConfirmDialog
        title="Excluir cliente?"
        open={Boolean(excluir)}
        setOpen={(v) => { if (!v) setExcluir(null); }}
        onConfirm={handleDeleteClick}
      >
        {excluir ? <><b>{excluir.name}</b> será excluído. Esta ação não pode ser desfeita.</> : null}
      </ConfirmDialog>
    </Container>
  );
}
